/**
 * Client for the Spaces API. Routes are implemented in a later phase; this
 * surface is stable for the create flow, directory, membership, invites, and
 * (non-E2EE) channel messaging.
 */
export class SpacesApi {
    client;
    constructor(client) {
        this.client = client;
    }
    // --- Space lifecycle ---
    async create(params) {
        return this.client.post('/api/spaces', params);
    }
    /**
     * Whether non-admin Space creation is enabled on the platform.
     * Platform admins can create regardless; combine with session.isPlatformAdmin in UI.
     */
    async getCreationEnabled() {
        return this.client.get('/api/spaces/creation-enabled');
    }
    /** Spaces the current identity is a member of. */
    async listMine() {
        return this.client.get('/api/spaces');
    }
    /**
     * Spaces the current identity is lurking (pinned to the sidebar without
     * joining). Never includes Spaces they have since joined.
     */
    async listLurking() {
        return this.client.get('/api/spaces/lurking');
    }
    /** Discover public spaces (never unlisted or hidden). Public; no auth required. */
    async discover(options) {
        const params = new URLSearchParams();
        if (options?.q)
            params.set('q', options.q);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.sample)
            params.set('sample', '1');
        const query = params.toString();
        return this.client.get(`/api/spaces/discover${query ? `?${query}` : ''}`);
    }
    async getBySlug(slug) {
        return this.client.get(`/api/spaces/slug/${encodeURIComponent(slug)}`);
    }
    /**
     * Public preview for a Space URL. Hidden and unknown slugs are
     * indistinguishable. No auth required.
     */
    async previewBySlug(slug) {
        return this.client.get(`/api/spaces/slug/${encodeURIComponent(slug)}/preview`);
    }
    /**
     * Signed-out browse view (profile + Everyone-open channel list) for a
     * public or non-E2EE unlisted Space. Anything else is not-found, matching
     * hidden and unknown slugs. No auth required.
     */
    async getGuestViewBySlug(slug) {
        return this.client.get(`/api/spaces/slug/${encodeURIComponent(slug)}/guest`);
    }
    async checkSlugAvailability(slug) {
        return this.client.get(`/api/spaces/slug/${encodeURIComponent(slug)}/available`);
    }
    async get(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}`);
    }
    async update(spaceId, params) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}`, params);
    }
    async delete(spaceId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}`);
    }
    /** Current viewer's membership and effective permissions in a Space. */
    async getMyPermissions(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/me`);
    }
    /** Admin-only Manage overview (stats + recent joins). */
    async getManageOverview(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/manage/overview`);
    }
    // --- Membership ---
    async join(spaceId, proof) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/join`, proof ?? {});
    }
    async leave(spaceId) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/leave`, {});
    }
    // --- Preferences ---
    async listPreferences() {
        return this.client.get('/api/spaces/preferences');
    }
    async updatePreferences(spaceId, patch) {
        return this.client.patch(`/api/spaces/preferences/${encodeURIComponent(spaceId)}`, patch);
    }
    async getThemeOverrides() {
        return this.client.get('/api/spaces/preferences/theme-overrides');
    }
    /** Clears every saved per-Space theme choice for the current alias. */
    async resetThemeOverrides() {
        return this.client.post('/api/spaces/preferences/reset-theme-overrides', {});
    }
    async listMembers(spaceId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.cohort)
            params.set('cohort', options.cohort);
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/members${query ? `?${query}` : ''}`);
    }
    async removeMember(spaceId, identityId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(identityId)}`);
    }
    async banMember(spaceId, identityId, body) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(identityId)}/ban`, body);
    }
    async unbanMember(spaceId, identityId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(identityId)}/ban`);
    }
    async listBannedMembers(spaceId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/members/banned${query ? `?${query}` : ''}`);
    }
    async getAuditLog(spaceId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/audit-log${query ? `?${query}` : ''}`);
    }
    /** Update Space-scoped nickname / colour for a member. Null clears a field. */
    async updateMemberProfile(spaceId, identityId, body) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(identityId)}/profile`, body);
    }
    // --- Roles ---
    async listRoles(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/roles`);
    }
    async createRole(spaceId, body) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/roles`, body);
    }
    async updateRole(spaceId, roleId, body) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}/roles/${encodeURIComponent(roleId)}`, body);
    }
    async reorderRoles(spaceId, roleIds) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/roles/order`, {
            roleIds,
        });
    }
    async deleteRole(spaceId, roleId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/roles/${encodeURIComponent(roleId)}`);
    }
    async listRoleMembers(spaceId, roleId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/roles/${encodeURIComponent(roleId)}/members${query ? `?${query}` : ''}`);
    }
    async setMemberRoles(spaceId, identityId, roleIds) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(identityId)}/roles`, { roleIds });
    }
    // --- Channels & messages ---
    async listChannels(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels`);
    }
    async createChannel(spaceId, body) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels`, body);
    }
    async updateChannel(spaceId, channelId, body) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}`, body);
    }
    async getChannelPermissions(spaceId, channelId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/me`);
    }
    async getChannelPermissionOverrides(spaceId, channelId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/permission-overrides`);
    }
    async setChannelPermissionOverrides(spaceId, channelId, body) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/permission-overrides`, body);
    }
    async listCategories(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/categories`);
    }
    async createCategory(spaceId, body) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/categories`, body);
    }
    async updateCategory(spaceId, categoryId, body) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}/categories/${encodeURIComponent(categoryId)}`, body);
    }
    async getCategoryPermissionOverrides(spaceId, categoryId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/categories/${encodeURIComponent(categoryId)}/permission-overrides`);
    }
    async setCategoryPermissionOverrides(spaceId, categoryId, body) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/categories/${encodeURIComponent(categoryId)}/permission-overrides`, body);
    }
    async deleteCategory(spaceId, categoryId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/categories/${encodeURIComponent(categoryId)}`);
    }
    async updateChannelLayout(spaceId, body) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/channel-layout`, body);
    }
    async getMessages(spaceId, channelId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.direction)
            params.set('direction', options.direction);
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages${query ? `?${query}` : ''}`);
    }
    /**
     * Durable unread + mention snapshot for every channel the member can view.
     * Only channels with unread messages or a pending mention are returned; the
     * client treats an absent channel as fully read. Call on load and reconnect.
     */
    async getUnread(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/unread`);
    }
    /** Advance the member's durable read cursor for a channel (monotonic). */
    async markChannelRead(spaceId, channelId, params) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/read`, params);
    }
    async sendMessage(spaceId, channelId, params, requestOptions) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages`, params, requestOptions);
    }
    async editMessage(spaceId, channelId, messageId, body) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`, body);
    }
    async deleteMessage(spaceId, channelId, messageId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`);
    }
    async modDeleteMessage(spaceId, channelId, messageId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}/mod`);
    }
    async getMessage(spaceId, channelId, messageId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`);
    }
    async getMessagesAround(spaceId, channelId, messageId, options) {
        const params = new URLSearchParams();
        if (options?.before != null)
            params.set('before', String(options.before));
        if (options?.after != null)
            params.set('after', String(options.after));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/around/${encodeURIComponent(messageId)}${query ? `?${query}` : ''}`);
    }
    /**
     * Thread root plus one page of replies (oldest-first). `before` is the
     * previous page's `cursor`; pass it to load older replies.
     */
    async getThread(spaceId, channelId, messageId, options) {
        const params = new URLSearchParams();
        if (options?.before)
            params.set('before', options.before);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}/thread${query ? `?${query}` : ''}`);
    }
    // --- Reactions ---
    async addReaction(spaceId, channelId, messageId, params) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}/reactions`, params);
    }
    async removeReaction(spaceId, channelId, messageId, reactionId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(reactionId)}`);
    }
    async getReactions(spaceId, channelId, messageId, options) {
        const params = new URLSearchParams();
        if (options?.tag)
            params.set('tag', options.tag);
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}/reactions${query ? `?${query}` : ''}`);
    }
    async getMyReactions(spaceId, channelId, messageIds) {
        const params = new URLSearchParams();
        if (messageIds.length > 0)
            params.set('messageIds', messageIds.join(','));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/reactions/mine${query ? `?${query}` : ''}`);
    }
    // --- Pins ---
    async pinMessage(spaceId, channelId, messageId) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/pins`, { messageId });
    }
    async unpinMessage(spaceId, channelId, messageId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/pins/${encodeURIComponent(messageId)}`);
    }
    async getPinnedMessages(spaceId, channelId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/pinned-messages${query ? `?${query}` : ''}`);
    }
    // --- Invites (mirrors group invites) ---
    async listInvites(limit, cursor) {
        const params = new URLSearchParams();
        if (limit)
            params.set('limit', String(limit));
        if (cursor)
            params.set('cursor', cursor);
        const query = params.toString();
        return this.client.get(`/api/spaces/invites${query ? `?${query}` : ''}`);
    }
    async getInvitePreview(inviteId) {
        return this.client.get(`/api/spaces/invites/${encodeURIComponent(inviteId)}/preview`);
    }
    async acceptInvite(inviteId, proof) {
        return this.client.post(`/api/spaces/invites/${encodeURIComponent(inviteId)}/accept`, proof ?? {});
    }
    async declineInvite(inviteId) {
        return this.client.post(`/api/spaces/invites/${encodeURIComponent(inviteId)}/decline`, {});
    }
    async createInvite(spaceId, identityId, options) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/invites`, {
            identityId,
            ...options,
        });
    }
    async listPendingInvites(spaceId, options) {
        const params = new URLSearchParams();
        if (options?.picker)
            params.set('picker', '1');
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/pending-invites${query ? `?${query}` : ''}`);
    }
    async revokeInvite(spaceId, inviteId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/invites/${encodeURIComponent(inviteId)}`);
    }
    // --- Invite links ---
    async createInviteLink(spaceId, options) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/invite-links`, options ?? {});
    }
    async listInviteLinks(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/invite-links`);
    }
    async listInviteHistory(spaceId, options) {
        const params = new URLSearchParams();
        if (options?.statuses && options.statuses.length > 0) {
            params.set('statuses', options.statuses.join(','));
        }
        if (options?.kinds && options.kinds.length > 0) {
            params.set('kinds', options.kinds.join(','));
        }
        if (options?.sort)
            params.set('sort', options.sort);
        if (options?.dir)
            params.set('dir', options.dir);
        if (options?.q)
            params.set('q', options.q);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.offset != null)
            params.set('offset', String(options.offset));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/invite-history${query ? `?${query}` : ''}`);
    }
    async listInviteLinkRedemptions(spaceId, linkId, options) {
        const params = new URLSearchParams();
        if (options?.q)
            params.set('q', options.q);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.offset != null)
            params.set('offset', String(options.offset));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/invite-links/${encodeURIComponent(linkId)}/redemptions${query ? `?${query}` : ''}`);
    }
    async revokeInviteLink(spaceId, linkId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/invite-links/${encodeURIComponent(linkId)}`);
    }
    /**
     * Public preview for a link code. Deliberately returns only what a visitor
     * needs to decide whether to join; unusable codes are indistinguishable.
     */
    async previewInviteLink(code) {
        return this.client.get(`/api/spaces/invite-links/${encodeURIComponent(code)}`);
    }
    /**
     * Space id (and Cipher challenge, when required) for an authenticated code
     * holder. Needed because the join proof is keyed by Space, and the anonymous
     * preview deliberately withholds the id.
     */
    async getInviteLinkChallenge(code) {
        return this.client.get(`/api/spaces/invite-links/${encodeURIComponent(code)}/challenge`);
    }
    async joinViaInviteLink(code, proof) {
        return this.client.post(`/api/spaces/invite-links/${encodeURIComponent(code)}/join`, proof ?? {});
    }
    // --- Voice channels ---
    async listVoicePresence(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/voice`);
    }
    async getVoiceSession(spaceId, channelId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/voice`);
    }
    async joinVoiceChannel(spaceId, channelId, media) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/voice/join`, media ?? {});
    }
    async leaveVoiceChannel(spaceId, channelId) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/voice/leave`, {});
    }
    async updateVoiceMedia(spaceId, channelId, media) {
        return this.client.patch(`/api/spaces/${encodeURIComponent(spaceId)}/channels/${encodeURIComponent(channelId)}/voice/media`, media);
    }
    // --- Activity ---
    /** Ranked feed of root messages and announcements the viewer can see. */
    async getActivity(spaceId, options) {
        const params = new URLSearchParams();
        if (options?.sort)
            params.set('sort', options.sort);
        if (options?.window)
            params.set('window', options.window);
        if (options?.include)
            params.set('include', options.include.join(','));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        const query = params.toString();
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/activity${query ? `?${query}` : ''}`);
    }
    async getActivitySettings(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/activity/settings`);
    }
    /** Requires `manageActivity`. Changed weights re-rank existing entries in the background. */
    async updateActivitySettings(spaceId, settings) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/activity/settings`, settings);
    }
    // --- Announcements ---
    async listAnnouncements(spaceId, options) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/announcements${announcementPageQuery(options)}`);
    }
    /**
     * Announcements anyone may read on a public (or non-E2EE unlisted) Space.
     * No auth required; hidden, E2EE, and unknown slugs all return NOT_FOUND.
     * Authors come back as opaque refs, never identity ids.
     */
    async listPublicAnnouncementsBySlug(slug, options) {
        return this.client.get(`/api/spaces/slug/${encodeURIComponent(slug)}/announcements${announcementPageQuery(options)}`);
    }
    async getAnnouncement(spaceId, announcementId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/announcements/${encodeURIComponent(announcementId)}`);
    }
    async postAnnouncement(spaceId, body) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/announcements`, body);
    }
    /** Replaces the announcement. `body.revision` must be the current revision + 1 (else 409). */
    async editAnnouncement(spaceId, announcementId, body) {
        return this.client.put(`/api/spaces/${encodeURIComponent(spaceId)}/announcements/${encodeURIComponent(announcementId)}`, body);
    }
    async deleteAnnouncement(spaceId, announcementId) {
        return this.client.delete(`/api/spaces/${encodeURIComponent(spaceId)}/announcements/${encodeURIComponent(announcementId)}`);
    }
    /** Latest unseen announcements to toast (oldest first) plus a count of the rest. */
    async listAnnouncementToasts(spaceId) {
        return this.client.get(`/api/spaces/${encodeURIComponent(spaceId)}/announcements/toasts`);
    }
    async dismissAnnouncement(spaceId, announcementId) {
        return this.client.post(`/api/spaces/${encodeURIComponent(spaceId)}/announcements/${encodeURIComponent(announcementId)}/dismiss`, {});
    }
}
function announcementPageQuery(options) {
    const params = new URLSearchParams();
    if (options?.before)
        params.set('before', options.before);
    if (options?.limit != null)
        params.set('limit', String(options.limit));
    const query = params.toString();
    return query ? `?${query}` : '';
}
//# sourceMappingURL=spaces-api.js.map